import React, { useEffect, useState } from "react";
import { Bell, MessageCircle, AtSign, Megaphone, X, CheckCircle2, Loader2 } from "lucide-react";
import {
  pushSupported,
  pushPermission,
  getPushSubscription,
  subscribeToPush,
} from "@/lib/push";

/**
 * One-time onboarding nudge that converts logged-in users into push subscribers.
 * Strategy:
 *   - Wait until user navigates to a community/chat/DM page (high-intent moment).
 *   - Show once per device. If user clicks "Neskôr", we re-show after 7 days.
 *   - Never show if permission already granted/denied — respect their choice.
 *   - Never show on /admin, /login, /landing — only inside community surfaces.
 */
const STORAGE_KEY = "tigik_push_onboard_v1";

function loadState() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch (_e) { return {}; }
}
function saveState(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (_e) { /* ignore */ }
}

export default function PushOnboardingModal() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only nudge inside community-like surfaces
      const path = window.location.pathname || "";
      const onCommunitySurface = /\/(community|profile|public|jobs|hall-of-fame|wall-of-fame|dm)/i.test(path);
      if (!onCommunitySurface) return;

      // Hard prerequisites: must be logged in (token present) + browser supports push
      const token = localStorage.getItem("wb_token");
      if (!token) return;
      if (!pushSupported()) return;

      // Respect prior choice — never re-prompt if user denied
      const perm = pushPermission();
      if (perm === "denied") return;

      // If permission already granted, verify subscription exists on backend
      if (perm === "granted") {
        try {
          const existing = await getPushSubscription();
          if (existing) return; // Already subscribed, nothing to do
          // Permission granted but NO subscription — silent re-subscribe attempt
          await subscribeToPush();
          return; // Success
        } catch (err) {
          // Subscription failed — fall through to show modal so user sees the error
          console.error("[Tigik Push] Silent subscribe failed:", err);
          if (!cancelled) {
            setError(err?.message || "Subscription failed (silent)");
            setOpen(true);
          }
          return;
        }
      }

      // Already onboarded? respect snooze window
      const s = loadState();
      if (s.completed) return;
      if (s.snoozedUntil && Date.now() < s.snoozedUntil) return;

      // 1.5s delay so the modal doesn't slap them in the face on first paint
      const t = setTimeout(() => { if (!cancelled) setOpen(true); }, 1500);
      return () => clearTimeout(t);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    setError("");
    try {
      await subscribeToPush();
      setDone(true);
      saveState({ completed: true, completedAt: Date.now() });
      setTimeout(() => setOpen(false), 1800);
    } catch (e) {
      setError(e?.message || "Niečo sa pokazilo. Skús to znova v Profile → Notifikácie.");
    } finally {
      setBusy(false);
    }
  };

  const handleLater = () => {
    // Snooze for 7 days
    saveState({ snoozedUntil: Date.now() + 7 * 24 * 60 * 60 * 1000 });
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="push-onboarding-modal"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleLater}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-zinc-950 border border-gold-700/40 shadow-2xl overflow-hidden"
        style={{ boxShadow: "0 20px 60px -10px rgba(212,175,55,0.25)" }}
      >
        {/* Close X */}
        <button
          onClick={handleLater}
          aria-label="Zavrieť"
          data-testid="push-onboarding-close"
          className="absolute top-2 right-2 p-1.5 text-zinc-500 hover:text-zinc-200 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Gold accent line */}
        <div className="h-1 bg-gradient-to-r from-gold-700 via-gold-500 to-gold-700" />

        <div className="p-6 sm:p-8">
          {/* Hero icon */}
          <div className="flex justify-center mb-5">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold-500 to-gold-700 flex items-center justify-center shadow-lg">
                <Bell className="w-8 h-8 text-zinc-950" />
              </div>
              {!done && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse">
                  1
                </div>
              )}
            </div>
          </div>

          {done ? (
            <div className="text-center space-y-3" data-testid="push-onboarding-success">
              <CheckCircle2 className="w-10 h-10 mx-auto text-green-400" />
              <h2 className="font-display text-xl font-black text-white">Hotovo!</h2>
              <p className="text-sm text-zinc-400">
                Notifikácie sú zapnuté. Žiadnu správu už neprehliadneš. 🎯
              </p>
            </div>
          ) : (
            <>
              <h2 className="font-display text-xl sm:text-2xl font-black text-white text-center leading-tight">
                Zapni si notifikácie
              </h2>
              <p className="text-center text-sm text-zinc-400 mt-2 mb-6">
                Žiadny spam — len keď ti niekto naozaj píše alebo sa deje niečo dôležité.
              </p>

              {/* What user gets */}
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-zinc-900 border border-gold-700/30 flex items-center justify-center">
                    <MessageCircle className="w-4 h-4 text-gold-400" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">Súkromné správy (DM)</div>
                    <div className="text-xs text-zinc-500">Niekto z komunity ti napíše osobne.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-zinc-900 border border-gold-700/30 flex items-center justify-center">
                    <AtSign className="w-4 h-4 text-gold-400" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">Označenia (@meno)</div>
                    <div className="text-xs text-zinc-500">Keď ťa niekto označí v chate.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-zinc-900 border border-gold-700/30 flex items-center justify-center">
                    <Megaphone className="w-4 h-4 text-gold-400" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">Tigik News 📢</div>
                    <div className="text-xs text-zinc-500">Oznamy od Sauberina — len naozaj dôležité veci.</div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="text-xs text-red-400 bg-red-950/40 border border-red-900/60 px-3 py-2 mb-3">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleEnable}
                  disabled={busy}
                  data-testid="push-onboarding-enable"
                  className="w-full bg-gold-500 hover:bg-gold-400 text-zinc-950 font-bold text-sm py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                  {busy ? "Zapínam…" : "Zapnúť notifikácie"}
                </button>
                <button
                  onClick={handleLater}
                  data-testid="push-onboarding-later"
                  className="w-full text-zinc-500 hover:text-zinc-300 text-xs py-2 transition-colors"
                >
                  Možno neskôr
                </button>
              </div>

              <p className="text-[10px] text-zinc-600 text-center mt-4 leading-relaxed">
                Notifikácie môžeš kedykoľvek vypnúť v <strong>Profil → Notifikácie</strong>.
                Žiadny marketing, žiadny spam. Sľubujem.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
